import { PaymentMethodBreakdown } from '@/lib/api/financial/financial-dashboard-api'
import { Cell, Pie, PieChart, ResponsiveContainer, Tooltip } from 'recharts'

interface PaymentMethodChartProps {
    data: PaymentMethodBreakdown[]
}

const formatCurrency = (cents: number) =>
    new Intl.NumberFormat('pt-BR', {
        style: 'currency',
        currency: 'BRL',
    }).format(cents / 100)

export default function PaymentMethodChart({ data }: PaymentMethodChartProps) {
    const total = data.reduce((acc, d) => acc + d.totalAmount, 0)

    return (
        <div className="bg-base-100 rounded-xl border border-base-300/50 p-5 flex flex-col">
            <h3 className="text-lg font-semibold text-base-content mb-4">
                Métodos de Pagamento
            </h3>

            {data.length === 0 || total === 0 ? (
                <div className="flex items-center justify-center h-64 text-base-content/50">
                    Sem dados no período
                </div>
            ) : (
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 items-center">
                    <div style={{ height: 220 }}>
                        <ResponsiveContainer width="100%" height="100%">
                            <PieChart>
                                <Pie
                                    data={data}
                                    dataKey="totalAmount"
                                    nameKey="label"
                                    innerRadius={50}
                                    outerRadius={90}
                                    paddingAngle={2}
                                >
                                    {data.map((entry, idx) => (
                                        <Cell key={idx} fill={entry.color} />
                                    ))}
                                </Pie>
                                <Tooltip
                                    formatter={(
                                        value:
                                            | number
                                            | string
                                            | readonly (number | string)[]
                                            | undefined,
                                    ) => formatCurrency(Number(value) || 0)}
                                    contentStyle={{
                                        backgroundColor: 'oklch(var(--b1))',
                                        border: '1px solid oklch(var(--b3))',
                                        borderRadius: 8,
                                    }}
                                />
                            </PieChart>
                        </ResponsiveContainer>
                    </div>

                    <ul className="flex flex-col gap-2">
                        {data.map(item => {
                            const pct = total > 0 ? (item.totalAmount / total) * 100 : 0
                            return (
                                <li
                                    key={item.method}
                                    className="flex items-center justify-between gap-3"
                                >
                                    <div className="flex items-center gap-2 min-w-0">
                                        <span
                                            className="w-3 h-3 rounded-full shrink-0"
                                            style={{ backgroundColor: item.color }}
                                        />
                                        <span className="text-sm truncate">
                                            {item.label}
                                        </span>
                                    </div>
                                    <div className="text-right shrink-0">
                                        <p className="text-sm font-semibold">
                                            {formatCurrency(item.totalAmount)}
                                        </p>
                                        <p className="text-xs text-base-content/60">
                                            {pct.toFixed(1)}% · {item.count}
                                        </p>
                                    </div>
                                </li>
                            )
                        })}
                    </ul>
                </div>
            )}
        </div>
    )
}
